import React, { useState } from "react";
import { Cpu, Loader2, Lock, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-sm animate-fadeup">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-sm bg-primary/15 border border-primary/40 flex items-center justify-center">
            <Cpu className="w-6 h-6 text-primary" />
          </div>
          <div>
            <div className="head text-lg font-bold tracking-tight text-ink leading-none">ORchestra</div>
            <div className="mono text-[10px] text-faint tracking-widest mt-1">CP-SAT SCHEDULING ENGINE</div>
          </div>
        </div>

        <div className="bg-surface border border-line rounded-sm p-6">
          <div className="mono text-[10px] uppercase tracking-widest text-faint mb-1">Restricted access</div>
          <h1 className="head text-xl font-semibold text-ink tracking-tight mb-5">Sign in to continue</h1>

          <form onSubmit={submit} className="space-y-4">
            <Input
              label="Email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@fenuanetwork.com"
              data-testid="login-email-input"
              required
            />
            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="login-password-input"
              required
            />

            {error && (
              <div
                className="flex items-start gap-2 text-xs text-critical bg-critical/10 border border-critical/30 rounded-sm px-3 py-2"
                data-testid="login-error"
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={busy}
              data-testid="login-submit-button"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {busy ? "Authenticating…" : "Sign in"}
            </Button>
          </form>
        </div>

        <div className="mono text-[10px] text-faint text-center mt-5 tracking-wider">
          Google OR-Tools · Secured session
        </div>
      </div>
    </div>
  );
}
